/**
 * Module Double Poutre - Composant Principal
 * 
 * Reprend exactement la même logique que StructureBaseModule
 * Interface utilisateur pour une structure indépendante plus haute
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  ParametresDoublePoutre,
  StructureDoublePoutre,
  DoublePoutreState
} from '../types';
import { TypeMateriauGlobal, GeometrieForme } from '../../structure-base/types';
import { callDoublePoutreAPI } from '../api/DoublePoutreAPI';
import { TraversesDoublePoutreModule, StructureTraversesDoublePoutre } from '../traverses';

// Styles CSS intégrés - Thème sombre identique à StructureBase
const styles = {
  container: {
    padding: '15px',
    backgroundColor: '#2d2d2d',
    borderRadius: '8px',
    border: '1px solid #404040',
    margin: '5px 0'
  } as React.CSSProperties,
  header: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '5px',
    color: '#4dabf7',
    borderBottom: '2px solid #4dabf7',
    paddingBottom: '3px'
  } as React.CSSProperties,
  description: {
    margin: '0 0 10px 0',
    color: '#aaa',
    fontSize: '12px'
  } as React.CSSProperties,
  formGroup: {
    marginBottom: '8px'
  } as React.CSSProperties,
  label: {
    display: 'block',
    marginBottom: '5px',
    fontWeight: '500',
    color: '#e0e0e0',
    fontSize: '13px'
  } as React.CSSProperties,
  checkbox: {
    marginRight: '8px'
  } as React.CSSProperties,
  sliderContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  } as React.CSSProperties,
  slider: {
    flex: 1,
    height: '6px',
    borderRadius: '3px',
    background: '#404040',
    outline: 'none',
    cursor: 'pointer'
  } as React.CSSProperties,
  value: {
    minWidth: '60px',
    textAlign: 'right' as const,
    fontWeight: '500',
    color: '#4dabf7'
  } as React.CSSProperties,
  error: {
    color: '#ff6b6b',
    fontSize: '12px',
    marginTop: '5px'
  } as React.CSSProperties,
  loading: {
    color: '#b0b0b0',
    fontStyle: 'italic'
  } as React.CSSProperties,
  info: {
    backgroundColor: '#1e3a5f',
    border: '1px solid #2c5aa0',
    borderRadius: '4px',
    padding: '10px',
    marginTop: '15px',
    fontSize: '12px',
    color: '#87ceeb'
  } as React.CSSProperties,
  statusZone: {
    minHeight: '25px',
    marginTop: '8px',
    display: 'flex',
    alignItems: 'center'
  } as React.CSSProperties,
  select: {
    width: '100%',
    padding: '8px',
    border: '1px solid #495057',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: '#333333',
    color: '#e0e0e0'
  } as React.CSSProperties,
  colorInput: {
    width: '60px',
    height: '40px',
    border: '1px solid #495057',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: 'transparent'
  } as React.CSSProperties
};

interface DoublePoutreModuleProps {
  actif: boolean;
  onToggle: (actif: boolean) => void;
  structurePrincipale?: {
    type: string;
    nombreCotes?: number;
    longueur: number;
    largeur: number;
    hauteur: number; // Hauteur de la structure principale
    materiau: TypeMateriauGlobal;
    couleur: string;
    epaisseur: number;
    montants: any[];
    poutres: {
      poutreHaute: {
        chemin: any[];
      };
    };
  };
  callbacks: {
    onStructureChange: (structure: StructureDoublePoutre | null) => void;
    onTraversesDoublePoutreChange?: (traverses: StructureTraversesDoublePoutre | null) => void;
    onSauvegarde: (config: any) => void;
  };
}

export const DoublePoutreModule: React.FC<DoublePoutreModuleProps> = ({
  actif,
  onToggle,
  structurePrincipale,
  callbacks
}) => {

  // État initial héritant de la structure principale
  const [state, setState] = useState<DoublePoutreState>({
    parametres: {
      active: false, // Désactivé par défaut
      hauteur: 0.8, // Limitée à 1m maximum
      nombreMontants: 4,
      materiau: structurePrincipale?.materiau || TypeMateriauGlobal.FER_A_BETON,
      epaisseur: structurePrincipale?.epaisseur || 0.1,
      couleur: structurePrincipale?.couleur || '#8B4513'
    },
    loading: false,
    error: undefined,
    initialized: false
  });

  const [nombreMontants, setNombreMontants] = useState(4);
  const [traversesDoublePoutre, setTraversesDoublePoutre] = useState<StructureTraversesDoublePoutre | null>(null);
  
  // Refs pour éviter les boucles infinies
  const structurePrincipaleRef = useRef(structurePrincipale);
  const callbacksRef = useRef(callbacks);
  
  // Référence pour le service 3D
  const serviceRef = useRef<any>(null);
  
  // Mettre à jour les refs
  useEffect(() => {
    structurePrincipaleRef.current = structurePrincipale;
    callbacksRef.current = callbacks;
  });

  // Synchroniser la prop actif avec l'état interne
  useEffect(() => {
    setState(prev => ({
      ...prev,
      parametres: {
        ...prev.parametres,
        active: actif
      }
    }));
  }, [actif]);

  /**
   * Génération de la structure avec isolation d'erreur
   * Reprend exactement la logique de StructureBase
   */
  const genererStructure = useCallback(async (params: ParametresDoublePoutre, forceStructure?: any) => {
    const currentStructure = forceStructure || structurePrincipale;
    const currentCallbacks = callbacksRef.current;
    

    
    if (!params.active || !currentStructure) {
      setState(prev => ({
        ...prev,
        structure: undefined,
        loading: false,
        error: undefined,
        initialized: true
      }));
      currentCallbacks?.onStructureChange?.(null);
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: undefined }));

      // Synchronisation automatique avec la structure principale
      const nouveauxParametres = {
        ...params,
        materiau: currentStructure.materiau,
        couleur: currentStructure.couleur,
        epaisseur: currentStructure.epaisseur / 1000 // Conversion mm vers m
      };

      // Génération locale avec les paramètres synchronisés
      
      // Utilisation directe du type de la structure principale
      const typeStructure = currentStructure.type;
      
      // Récupération des points corrects depuis la structure principale
      let pointsCorrects = [];
      if (currentStructure.poutres?.poutreHaute?.chemin && currentStructure.poutres.poutreHaute.chemin.length > 0) {
        pointsCorrects = currentStructure.poutres.poutreHaute.chemin;
      }
      
      const formeBase: GeometrieForme = {
        type: typeStructure,
        longueur: currentStructure.longueur,
        largeur: currentStructure.largeur,
        nombreCotes: currentStructure.nombreCotes,
        points: pointsCorrects,
        hauteur: 0.2
      };
      

      
      const hauteurStructurePrincipale = currentStructure.hauteur || 2.5;
      
      // Utilisation du service 3D local avec les paramètres synchronisés
      if (!serviceRef.current) {
        serviceRef.current = new (await import('../services/DoublePoutre3DService')).DoublePoutre3DService();
      }
      await serviceRef.current.genererStructure(nouveauxParametres, formeBase, hauteurStructurePrincipale);
      
      // Récupération de la structure générée avec les montants
      const structureGeneree = serviceRef.current.getStructure();
      
      if (!structureGeneree) {
        throw new Error('Échec de la génération de la structure');
      }
      
      // Utilisation de la structure générée avec la géométrie 3D
      const structure: StructureDoublePoutre = {
        ...structureGeneree,
        geometry: serviceRef.current.getGroupe()
      };
      
      setState(prev => ({
          ...prev,
          structure,
          isLoading: false,
          error: undefined,
          initialized: true
       }));

      // Mettre à jour le slider avec le nombre réel de montants
      if (structure.nombreMontants && structure.nombreMontants !== params.nombreMontants) {
        setNombreMontants(structure.nombreMontants);
      }

      if (structure.error) {
        // Gestion d'erreur via callbacks si disponible
      } else {
        currentCallbacks?.onStructureChange?.(structure);
      }

    } catch (error) {
      const erreurMsg = `Erreur module double-poutre: ${error instanceof Error ? error.message : 'Erreur inconnue'}`;
      setState(prev => ({ ...prev, loading: false, error: erreurMsg }));
      // Gestion d'erreur via callbacks si disponible
    }
  }, [structurePrincipale]);

  // Références pour éviter les re-renders inutiles comme dans TraversesModule
  const isGeneratingRef = useRef(false);
  const previousParametresRef = useRef<ParametresDoublePoutre | null>(null);
  const previousStructureRef = useRef<any>(null);

  /**
   * Mise à jour des paramètres avec synchronisation de la prop actif
   */
  const updateParametres = useCallback((nouveauxParams: Partial<ParametresDoublePoutre>) => {
    setState(prev => {
      const params = { ...prev.parametres, ...nouveauxParams };
      return { ...prev, parametres: params };
    });
    
    // Synchroniser avec la prop actif si elle change
    if ('active' in nouveauxParams && nouveauxParams.active !== undefined) {
      onToggle(nouveauxParams.active);
    }
  }, [onToggle]);

  /**
   * Mise à jour du nombre de montants
   */
  const updateNombreMontants = useCallback((nombre: number) => {
    setNombreMontants(nombre);
    setState(prev => {
      const params = { ...prev.parametres, nombreMontants: nombre };
      return { ...prev, parametres: params };
    });
  }, []);

  // Synchroniser les paramètres avec la structure principale
  useEffect(() => {
    if (structurePrincipale && actif) {
      updateParametres({
        materiau: structurePrincipale.materiau,
        couleur: structurePrincipale.couleur,
        epaisseur: structurePrincipale.epaisseur
      });
    }
  }, [structurePrincipale, actif, updateParametres]);

  // Synchronisation avec la prop actif (optimisée)
  useEffect(() => {
    if (actif !== state.parametres.active) {
      setState(prev => ({
        ...prev,
        parametres: { ...prev.parametres, active: actif }
      }));
    }
  }, [actif, state.parametres.active]);

  // Mémoriser l'objet donneesStructureDoublePoutre pour éviter les re-créations
  const donneesStructureDoublePoutre = useMemo(() => {
    if (!state.structure) return null;
    
    return {
      forme: structurePrincipale?.type || 'rectangle',
      largeur: structurePrincipale?.largeur || 0,
      longueur: structurePrincipale?.longueur || 0,
      hauteur: state.parametres.hauteur,
      hauteurStructurePrincipale: structurePrincipale?.hauteur || 2.5,
      nombreMontants: state.structure.nombreMontants,
      positionsMontants: state.structure.montants.map(m => ({
        x: m.position.x,
        y: m.position.y,
        z: m.position.z
      })),
      materiau: state.parametres.materiau === TypeMateriauGlobal.FER_A_BETON ? 'fer_a_beton' : 'tube_carre',
      epaisseur: state.parametres.epaisseur,
      couleur: state.parametres.couleur,
      poutreHaute: {
        points: state.structure.poutres.poutreHaute.chemin.map(p => ({
          x: p.x,
          y: p.y,
          z: p.z
        }))
      },
      poutreBasse: {
        points: state.structure.poutres.poutreBasse.chemin.map(p => ({
          x: p.x,
          y: p.y,
          z: p.z
        }))
      }
    };
  }, [
    state.structure,
    structurePrincipale?.type,
    structurePrincipale?.largeur,
    structurePrincipale?.longueur,
    structurePrincipale?.hauteur,
    state.parametres.hauteur,
    state.parametres.materiau,
    state.parametres.epaisseur,
    state.parametres.couleur
  ]);

  // Synchronisation des paramètres avec la structure principale
  useEffect(() => {
    if (structurePrincipale && state.parametres.active) {
      const nouveauxParametres = {
        ...state.parametres,
        materiau: structurePrincipale.materiau,
        couleur: structurePrincipale.couleur,
        epaisseur: structurePrincipale.epaisseur / 1000 // Conversion mm vers m
      };
      
      // Vérifier si les paramètres ont vraiment changé
      const parametresOntChange = 
        state.parametres.materiau !== nouveauxParametres.materiau ||
        state.parametres.couleur !== nouveauxParametres.couleur ||
        Math.abs(state.parametres.epaisseur - nouveauxParametres.epaisseur) > 0.001;
      
      if (parametresOntChange) {
        setState(prev => ({ ...prev, parametres: nouveauxParametres }));
      }
    }
  }, [structurePrincipale?.materiau, structurePrincipale?.couleur, structurePrincipale?.epaisseur, state.parametres.active]);

  // Génération avec vérification des changements comme TraversesModule
  useEffect(() => {
    // Éviter les boucles infinies
    if (isGeneratingRef.current) {
      return;
    }
    
    // Vérifier si les paramètres ont réellement changé
    const currentParams = state.parametres;
    const previousParams = previousParametresRef.current;
    const previousStructure = previousStructureRef.current;
    
    // Vérifier changements paramètres qui nécessitent une regénération complète
    const parametresChanged = !previousParams || 
        previousParams.active !== currentParams.active ||
        previousParams.hauteur !== currentParams.hauteur ||
        previousParams.nombreMontants !== currentParams.nombreMontants ||
        previousParams.materiau !== currentParams.materiau;
    
    // Vérifier changements structure principale
    const structureChanged = !previousStructure ||
        !structurePrincipale ||
        previousStructure.largeur !== structurePrincipale.largeur ||
        previousStructure.longueur !== structurePrincipale.longueur ||
        previousStructure.hauteur !== structurePrincipale.hauteur ||
        previousStructure.materiau !== structurePrincipale.materiau ||
        previousStructure.type !== structurePrincipale.type ||
        previousStructure.montants?.length !== structurePrincipale.montants?.length;
    
    // Vérifier changements d'épaisseur et couleur pour mise à jour en temps réel
    const epaisseurChanged = previousParams && 
        Math.abs(previousParams.epaisseur - currentParams.epaisseur) > 0.001;
    const couleurChanged = previousParams && 
        previousParams.couleur !== currentParams.couleur;
    
    // Mise à jour en temps réel pour épaisseur et couleur si structure existe
    if ((epaisseurChanged || couleurChanged) && serviceRef.current && state.structure && currentParams.active && !parametresChanged && !structureChanged) {
      if (couleurChanged) {
        serviceRef.current.updateCouleur(currentParams.couleur);
      }
      if (epaisseurChanged) {
        serviceRef.current.updateEpaisseur(currentParams.epaisseur);
      }
      // Sauvegarder les nouveaux paramètres
      previousParametresRef.current = { ...currentParams };
      return;
    }
    
    if (!parametresChanged && !structureChanged) {
      return; // Aucun changement réel
    }
    
    // Sauvegarder les paramètres et structure actuels
    previousParametresRef.current = { ...currentParams };
    previousStructureRef.current = structurePrincipale ? { ...structurePrincipale } : null;
    
    if (currentParams.active && structurePrincipale) {
      isGeneratingRef.current = true;
      genererStructure(currentParams).finally(() => {
        isGeneratingRef.current = false;
      });
    } else if (!currentParams.active) {
      // Nettoyer la structure si désactivée
      callbacksRef.current?.onStructureChange?.(null);
    }
  }, [state.parametres.active, state.parametres.hauteur, state.parametres.nombreMontants, state.parametres.epaisseur, state.parametres.couleur, state.parametres.materiau, structurePrincipale, genererStructure]);

  // La génération est maintenant gérée par le useEffect avec vérification des changements ci-dessus



  return (
    <div style={styles.container}>
      <div style={styles.header}>
        🏗️ Module Double Poutre
      </div>
      <div style={styles.description}>
        Structure synchronisée avec la structure principale - Hauteur et nombre de montants configurables
      </div>

      {/* Activation du module */}
      <div style={styles.formGroup}>
        <label style={styles.label}>
          <input
            type="checkbox"
            style={styles.checkbox}
            checked={actif}
            onChange={(e) => {
              const newActive = e.target.checked;
              onToggle(newActive);
            }}
          />
          Activer la double poutre
        </label>
      </div>

      {state.parametres.active && (
        <>
          {/* Slider Hauteur */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Hauteur de la double poutre:</label>
            <div style={styles.sliderContainer}>
              <input
                type="range"
                style={styles.slider}
                min="0.5"
                max="1"
                step="0.1"
                value={state.parametres.hauteur}
                onChange={(e) => updateParametres({ hauteur: parseFloat(e.target.value) })}
              />
              <span style={styles.value}>{state.parametres.hauteur.toFixed(1)}m</span>
            </div>
          </div>

          {/* Slider Nombre de montants */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Nombre de montants:</label>
            <div style={styles.sliderContainer}>
              <input
                type="range"
                style={styles.slider}
                min="4"
                max="50"
                step="1"
                value={nombreMontants}
                onChange={(e) => updateNombreMontants(parseInt(e.target.value))}
              />
              <span style={styles.value}>{nombreMontants}</span>
            </div>
          </div>

          {/* Module des traverses de la double poutre */}
          {donneesStructureDoublePoutre && (
            <TraversesDoublePoutreModule
              donneesStructureDoublePoutre={donneesStructureDoublePoutre}
              onTraversesChange={(traverses: StructureTraversesDoublePoutre | null) => {
                // Stocker les traverses dans l'état
                setTraversesDoublePoutre(traverses);
                // Notifier le composant parent
                callbacks?.onTraversesDoublePoutreChange?.(traverses);
                // Traverses de la double poutre mises à jour
              }}
              onError={(error: string) => {
                setState(prev => ({ ...prev, error }));
              }}
            />
          )}

        </>
      )}

      {/* Zone de statut */}
      <div style={styles.statusZone}>
        {state.loading && <span style={styles.loading}>Génération en cours...</span>}
        {state.error && <div style={styles.error}>{state.error}</div>}
      </div>


    </div>
  );
};

export default DoublePoutreModule;